import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PublicNav from '@/components/PublicNav';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fullscreen photo viewer — arrows / keyboard / swipe to move through the gallery.
function Lightbox({ photos, index, onClose, onIndex }) {
  const count = photos.length;
  const go = useCallback((delta) => onIndex((index + delta + count) % count), [index, count, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    if (Math.abs(dx) > 40 && count > 1) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0a0a0f]/[0.97]"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button onClick={onClose} className="absolute top-5 right-5 z-10 text-white/60 hover:text-white transition-colors" aria-label="Close">
        <X size={28} />
      </button>
      {count > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-2 md:left-6 z-10 p-2 text-white/50 hover:text-white transition-colors" aria-label="Previous">
            <ChevronLeft size={34} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-2 md:right-6 z-10 p-2 text-white/50 hover:text-white transition-colors" aria-label="Next">
            <ChevronRight size={34} />
          </button>
        </>
      )}
      <img
        src={photos[index]}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain select-none"
      />
      {count > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono tracking-widest text-white/50">
          {index + 1} / {count}
        </div>
      )}
    </div>
  );
}

export default function TravelsPostcard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [postcard, setPostcard] = useState(null);
  const [error, setError] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/giraffe/postcard/${slug}`)
      .then(r => {
        setPostcard(r.data);
        document.title = `${r.data.title || 'Postcard'} · Werner du Toit`;
      })
      .catch(() => setError(true));
  }, [slug]);


  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16">
        <PublicNav />
        <div className="flex items-center justify-center p-6 pt-24">
          <div className="text-center">
            <h1 className="text-2xl text-[#f0f0f5] font-serif mb-2">Not Found</h1>
            <p className="text-[#b0b4c0] font-light">This postcard doesn't exist or isn't public.</p>
            <a href="/travels" className="inline-block mt-6 text-xs font-mono tracking-[0.2em] uppercase text-[#10B981] hover:text-[#10B981]/80 transition-colors">
              Browse the gallery →
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!postcard) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16">
        <PublicNav />
        <ZLoadingBar accent="#10B981" loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20">
      <PublicNav />

      <div className="px-6 md:px-12 max-w-2xl mx-auto flex flex-col items-center">
        {postcard.composed_image_url && (
          <img
            src={postcard.composed_image_url}
            alt={postcard.title || ''}
            className="w-full max-w-[520px] rounded-lg border border-white/[0.06] shadow-2xl mt-6"
          />
        )}

        {/* Title/body are baked into the composed postcard image — only the
            story (not on the image) is rendered here to avoid duplication. */}
        {postcard.story && (
          <div className="postcard-story mt-8 max-w-lg w-full">
            {postcard.story.split(/\n\n+/).map((para, i) => (
              <p
                key={i}
                className="postcard-story-text text-[#d8dae0] font-light mb-4 whitespace-pre-line"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {para}
              </p>
            ))}
          </div>
        )}

        {/* Photo gallery — under the story; tap a photo to view full size and scroll through */}
        {Array.isArray(postcard.gallery) && postcard.gallery.length > 0 && (
          <div className="w-full max-w-lg mt-10 pt-8 border-t border-white/[0.06]">
            <p className="text-center text-[10px] font-mono tracking-[0.25em] uppercase text-[#b0b4c0]/40 mb-4">
              {postcard.gallery.length} photo{postcard.gallery.length === 1 ? '' : 's'}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {postcard.gallery.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  className="group aspect-square overflow-hidden rounded-md border border-white/[0.06] bg-black/20"
                  aria-label={`View photo ${i + 1}`}
                >
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => navigate('/travels/postcards')}
          className="mt-10 text-xs font-mono tracking-[0.2em] uppercase text-[#b0b4c0]/60 hover:text-[#b0b4c0] transition-colors"
        >
          ← All postcards
        </button>
      </div>

      {lightboxIndex !== null && Array.isArray(postcard.gallery) && postcard.gallery.length > 0 && (
        <Lightbox
          photos={postcard.gallery}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndex={setLightboxIndex}
        />
      )}
    </div>
  );
}
